import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { NgIcon } from '@ng-icons/core';
import { RouteNavbar } from '@shared/components/navigation/interfaces/routes-navbar';

interface SearchItem {
  title: string;
  parent: string | null;
  path: string;
  icon: string;
  /** Texto normalizado (sin acentos, minúsculas) sobre el que se busca */
  haystack: string;
  normalizedTitle: string;
}

/** Tramo del título; `match` marca lo que coincide con la búsqueda */
interface TitleSegment {
  text: string;
  match: boolean;
}

interface SearchResult extends SearchItem {
  segments: TitleSegment[];
}

/** Quita acentos y pasa a minúsculas para comparar sin importar tildes */
const normalize = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

/**
 * Buscador global de secciones del menú (estilo Odoo / Ctrl+K).
 *
 * Solo indexa las rutas que recibe, que son las mismas del sidebar: ya vienen
 * filtradas por el rol del usuario desde `Permissions.getVisibleRoutes`, así
 * que nunca ofrece una sección sin permiso. Todo el filtrado es local.
 */
@Component({
  selector: 'app-global-search',
  imports: [NgIcon],
  templateUrl: './global-search.html',
  styleUrl: './global-search.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:keydown)': 'onDocumentKeydown($event)' },
})
export class GlobalSearch {
  private router = inject(Router);

  routes = input.required<RouteNavbar[]>();

  open = signal(false);
  query = signal('');
  activeIndex = signal(0);

  private searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  private resultsList = viewChild<ElementRef<HTMLElement>>('resultsList');

  /** Aplana el árbol: los dropdowns no navegan, solo sus hijos */
  private items = computed<SearchItem[]>(() => {
    const flat: SearchItem[] = [];
    const add = (route: RouteNavbar, parent: string | null) => {
      flat.push({
        title: route.title,
        parent,
        path: route.path,
        icon: route.iconOutline,
        haystack: normalize(`${parent ?? ''} ${route.title} ${route.path}`),
        normalizedTitle: normalize(route.title),
      });
    };

    for (const route of this.routes()) {
      if (route.isDropdown) {
        route.children?.forEach(child => add(child, route.title));
      } else {
        add(route, null);
      }
    }
    return flat;
  });

  results = computed<SearchResult[]>(() => {
    const q = normalize(this.query());
    if (!q) {
      return this.items().map(item => ({
        ...item,
        segments: [{ text: item.title, match: false }],
      }));
    }

    const tokens = q.split(/\s+/);
    return this.items()
      .filter(item => tokens.every(t => item.haystack.includes(t)))
      .map(item => ({ item, score: this.score(item, q) }))
      .sort((a, b) => b.score - a.score)
      .map(({ item }) => ({
        ...item,
        segments: this.highlight(item, tokens),
      }));
  });

  /** Ctrl en Windows/Linux, ⌘ en Mac, para las pistas de teclado */
  readonly modifierKey = /Mac|iPhone|iPad/.test(navigator.userAgent)
    ? '⌘'
    : 'Ctrl';

  /**
   * Parte el título en tramos marcando las coincidencias. Se compara sobre el
   * texto normalizado, que conserva la longitud del original (las tildes en
   * NFC son un solo carácter); si no la conserva, no se resalta.
   */
  private highlight(item: SearchItem, tokens: string[]): TitleSegment[] {
    const { title, normalizedTitle } = item;
    if (normalizedTitle.length !== title.trim().length) {
      return [{ text: title, match: false }];
    }

    const offset = title.length - title.trimStart().length;
    const marked = new Array<boolean>(title.length).fill(false);
    for (const token of tokens) {
      let from = normalizedTitle.indexOf(token);
      while (from !== -1) {
        marked.fill(true, offset + from, offset + from + token.length);
        from = normalizedTitle.indexOf(token, from + token.length);
      }
    }

    const segments: TitleSegment[] = [];
    for (let i = 0; i < title.length; i++) {
      const last = segments.at(-1);
      if (last && last.match === marked[i]) {
        last.text += title[i];
      } else {
        segments.push({ text: title[i], match: marked[i] });
      }
    }
    return segments;
  }

  private score(item: SearchItem, q: string): number {
    const title = item.normalizedTitle;
    if (title === q) return 4;
    if (title.startsWith(q)) return 3;
    if (title.split(/\s+/).some(word => word.startsWith(q))) return 2;
    if (title.includes(q)) return 1;
    return 0;
  }

  onDocumentKeydown(event: KeyboardEvent) {
    if (this.open()) return;

    const isShortcut =
      (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k';
    if (isShortcut) {
      event.preventDefault();
      this.show('');
      return;
    }

    // Empezar a escribir fuera de un campo abre el buscador con esa letra
    const isPrintable =
      event.key.length === 1 &&
      /\S/.test(event.key) &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey;
    if (isPrintable && !this.isTypingElsewhere(event)) {
      event.preventDefault();
      this.show(event.key);
    }
  }

  /**
   * Evita robar teclas de inputs, editores (Quill), selects con type-ahead
   * (PrimeNG enfoca un elemento role="combobox") u overlays abiertos
   */
  private isTypingElsewhere(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement | null;
    if (
      target?.closest(
        'input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="combobox"], [role="listbox"], [role="menu"]'
      )
    ) {
      return true;
    }
    return !!document.querySelector(
      '.p-dialog-mask, .p-drawer-mask, .p-overlay-mask, [role="dialog"]'
    );
  }

  show(initialQuery: string) {
    this.query.set(initialQuery);
    this.activeIndex.set(0);
    this.open.set(true);
    // Esperar a que el @if pinte el input antes de enfocarlo
    setTimeout(() => {
      const el = this.searchInput()?.nativeElement;
      if (!el) return;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    });
  }

  close() {
    this.open.set(false);
  }

  clear() {
    this.onQueryChange('');
    this.searchInput()?.nativeElement.focus();
  }

  onQueryChange(value: string) {
    this.query.set(value);
    this.activeIndex.set(0);
  }

  onInputKeydown(event: KeyboardEvent) {
    const total = this.results().length;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (total) this.setActive((this.activeIndex() + 1) % total);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (total) this.setActive((this.activeIndex() - 1 + total) % total);
        break;
      case 'Enter': {
        event.preventDefault();
        const item = this.results()[this.activeIndex()];
        if (item) this.go(item);
        break;
      }
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
    }
  }

  private setActive(index: number) {
    this.activeIndex.set(index);
    this.resultsList()
      ?.nativeElement.querySelector(`[data-index="${index}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }

  go(item: SearchItem) {
    this.close();
    this.router.navigateByUrl(item.path);
  }
}
